import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import styles from "./index.module.css";
import Logo from "../../../Utils/icons/eden_Care_Logo.png";
import newLogo from "../../../Utils/icons/newLogo.png";
import { canAccessRoute } from "../../../Utils/access/permissionHelpers";
import { sidebarNavGroups } from "./sidebarNavConfig";

const NavItem = ({ item, open, index }) => {
  const isExternal = item.external;
  const content = (
    <motion.span
      className={styles.navItemInner}
      style={{ justifyContent: open ? "flex-start" : "center" }}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{
        duration: 0.6,
        delay: index * 0.08,
        ease: [0.25, 0.1, 0.25, 1],
      }}
    >
      <motion.img
        className={styles.sidebarIcon}
        src={item.icon}
        alt=""
        aria-hidden
        whileHover={{ scale: 1.15 }}
        transition={{ type: "spring", stiffness: 300, damping: 15 }}
      />
      <AnimatePresence>
        {open && (
          <motion.span
            className={styles.itemLabel}
            initial={{ opacity: 0, x: -16, filter: "blur(6px)" }}
            animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
            exit={{ opacity: 0, x: -16, filter: "blur(6px)" }}
            transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
          >
            {item.name}
          </motion.span>
        )}
      </AnimatePresence>
    </motion.span>
  );

  if (isExternal && item.path === "/ucare") {
    return (
      <a
        href="https://ucare-tau.vercel.app/"
        target="_blank"
        rel="noopener noreferrer"
        className={styles.linkItem}
        aria-label={`Open ${item.name} in new tab`}
      >
        {content}
      </a>
    );
  }

  return (
    <NavLink
      to={item.path}
      end={item.path === "/"}
      className={({ isActive }) =>
        isActive
          ? `${styles.linkItem} ${styles.linkItemActive}`
          : styles.linkItem
      }
    >
      {content}
    </NavLink>
  );
};

const SideBar = () => {
  const [open, setOpen] = useState(true);
  const logoUrl = useSelector((s) => s.brand.logoUrl);
  const permissionKeys = useSelector((s) => s.access?.permissionKeys) || [];

  const navGroups = useMemo(() => {
    return sidebarNavGroups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => {
          if (item.access?.public) return true;
          return canAccessRoute({
            permissionKeys,
            requiredAny: item.access?.requiredAny,
            requirePrivileged: item.access?.requirePrivileged,
            orPrivilege: item.access?.orPrivilege,
          });
        }),
      }))
      .filter((g) => g.items.length > 0);
  }, [permissionKeys]);

  const displayLogoSrc = logoUrl || (open ? Logo : newLogo);
  const logoAlt = "Company logo";

  return (
    <motion.aside
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      className={open ? styles.sideBarOpen : styles.sideBar}
      initial={false}
      animate={{ width: open ? 260 : 80 }}
      transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
    >
      <Link
        to="/"
        className={styles.logoLink}
        aria-label="Go to Dashboard — Sales"
      >
        <motion.img
          src={displayLogoSrc}
          className={styles.logo}
          alt={logoAlt}
          key={logoUrl ? logoUrl : open ? "full" : "mini"}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        />
        <AnimatePresence>
          {open && (
            <motion.span
              className={styles.logoProductLabel}
              initial={{ opacity: 0, x: -8, filter: "blur(4px)" }}
              animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, x: -8, filter: "blur(4px)" }}
              transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
            >
              Sales
            </motion.span>
          )}
        </AnimatePresence>
      </Link>

      <nav
        className={styles.nav}
        aria-label="Main navigation"
        style={{ marginTop: "40px" }}
      >
        <div className={styles.navScroll}>
          {navGroups.map((group) => (
            <div key={group.label} className={styles.navGroup}>
              <AnimatePresence>
                {open && (
                  <motion.span
                    className={styles.groupLabel}
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.45, ease: [0.25, 0.1, 0.25, 1] }}
                  >
                    {group.label}
                  </motion.span>
                )}
              </AnimatePresence>
              <div className={styles.groupItems}>
                {group.items.map((item, i) => (
                  <NavItem key={item.name} item={item} open={open} index={i} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </nav>
    </motion.aside>
  );
};

export default SideBar;
